import type { UnlockedContent } from './data/unlocks.js';
import {
  DEFAULT_RUN_CONFIG,
  type EnemyTable,
  type RunConfig,
  type ClassTable,
  type TowerSpec,
  type WaveSpec,
} from '@prism/sim';
import { BALANCE } from './data/balance.js';
import { CLASSES } from './data/classes.js';
import { ENEMIES } from './data/enemies.js';
import { MAPS } from './data/maps.js';
import { TOWERS } from './data/towers.js';
import { WAVES } from './data/waves.js';

/**
 * The bridge from authored content to a runnable `RunConfig`.
 *
 * `packages/sim` cannot import this package (TRD-00 §4.1), so it declares structural
 * shapes in `scenario.ts` that these Zod-inferred types satisfy. This function is the one
 * place that knows both sides — and it stays in `content` rather than `client` because the
 * server needs the identical assembly to replay a submitted run (TRD-11 §2).
 *
 * Note what is NOT here: no lookup by id inside the sim, no content import at runtime.
 * Everything a run needs is copied into the config as plain data, which is exactly what
 * lets the server rebuild a run from storage without this package (TRD-01 R-01-14).
 */

export interface ScenarioOptions {
  mapId?: string;
  /**
   * A generated map, supplied directly instead of looked up by id (TRD-13).
   *
   * Generated dailies have no entry in the authored map table, and forcing them through
   * the lookup would mean either registering throwaway maps or smuggling the fields in as
   * overrides — both of which hide what is actually happening.
   */
  map?: {
    id: string;
    paths: RunConfig['paths'];
    core: RunConfig['core'];
    buildMask: readonly string[];
  };
  isDaily?: boolean;
  /** Restrict the draftable pool — the meta unlock tree does this (TRD-08 §8). */
  towerPool?: readonly string[];
  /** Override the authored wave table. Daily runs pass generated waves here (TRD-13 §5). */
  waves?: readonly WaveSpec[];
  overrides?: Partial<RunConfig>;
}

/** Enemy archetypes keyed by id, in the reduced shape the sim needs. */
export function buildEnemyTable(): EnemyTable {
  const table: Record<string, EnemyTable[string]> = {};
  for (const e of ENEMIES) {
    table[e.id] = {
      id: e.id,
      hp: e.hp,
      armor: e.armor,
      speed: e.speed,
      bounty: e.bounty,
      leakDamage: e.leakDamage,
      size: e.size,
      flags: e.flags,
      ...(e.splitInto !== undefined ? { splitInto: e.splitInto } : {}),
      ...(e.splitHpFraction !== undefined ? { splitHpFraction: e.splitHpFraction } : {}),
      ...(e.shieldPerHit !== undefined ? { shieldPerHit: e.shieldPerHit } : {}),
      ...(e.shieldHp !== undefined ? { shieldHp: e.shieldHp } : {}),
      ...(e.shieldDownTicks !== undefined ? { shieldDownTicks: e.shieldDownTicks } : {}),
      ...(e.healRadius !== undefined ? { healRadius: e.healRadius } : {}),
      ...(e.healPercentPerSecond !== undefined
        ? { healPercentPerSecond: e.healPercentPerSecond }
        : {}),
      ...(e.leapEveryTicks !== undefined ? { leapEveryTicks: e.leapEveryTicks } : {}),
      ...(e.leapTiles !== undefined ? { leapTiles: e.leapTiles } : {}),
      ...(e.statusImmunities !== undefined ? { statusImmunities: e.statusImmunities } : {}),
      ...(e.emergeTicks !== undefined ? { emergeTicks: e.emergeTicks } : {}),
      ...(e.teleportEveryTicks !== undefined
        ? { teleportEveryTicks: e.teleportEveryTicks }
        : {}),
      ...(e.frontalCos !== undefined ? { frontalCos: e.frontalCos } : {}),
      ...(e.frontalReduction !== undefined ? { frontalReduction: e.frontalReduction } : {}),
      ...(e.disableRange !== undefined ? { disableRange: e.disableRange } : {}),
      ...(e.disableRadius !== undefined ? { disableRadius: e.disableRadius } : {}),
      ...(e.disableTicks !== undefined ? { disableTicks: e.disableTicks } : {}),
      ...(e.disableEveryTicks !== undefined ? { disableEveryTicks: e.disableEveryTicks } : {}),
    };
  }
  return table;
}

/** Tower definitions reduced to what the sim needs to place and fire one. */
export function buildTowerPool(ids?: readonly string[]): TowerSpec[] {
  const allowed = ids ? new Set(ids) : null;
  return TOWERS.filter((t) => allowed === null || allowed.has(t.id)).map((t) => ({
    id: t.id,
    tier: t.tier,
    classes: t.classes,
    base: {
      damage: t.base.damage,
      damageKind: t.base.damageKind,
      attackInterval: t.base.attackInterval,
      range: t.base.range,
      // The WHOLE projectile, spread rather than field-picked. The old hand-copied
      // subset silently dropped every firing-approach field (salvo, burst, pierce,
      // acceleration, laser ramp) at this bridge, so the authored weapons played as
      // plain single-shot guns in every real run — the tests never caught it because
      // they hand-build TowerSpecs and skip this function.
      projectile: { ...t.base.projectile },
      defaultTargeting: t.base.defaultTargeting,
    },
    starScaling: t.starScaling,
    rangeScaling: t.rangeScaling,
    ...(t.support ? { support: true } : {}),
    ...(t.innateEffects.length > 0 ? { innateEffects: t.innateEffects } : {}),
  }));
}

/**
 * Class definitions in the shape the synergy engine consumes.
 *
 * Effects pass through untouched — they are already the declarative union the sim
 * evaluates, which is the whole reason TRD-08 insisted they be data (R-08-07).
 */
export function buildClassTable(): ClassTable {
  const table: Record<string, ClassTable[string]> = {};
  for (const c of CLASSES) {
    table[c.id] = {
      id: c.id,
      name: c.name,
      tiers: [c.tiers[0], c.tiers[1], c.tiers[2]],
    };
  }
  return table;
}

/**
 * A free-play RunConfig shaped by the player's unlock tree (TRD-08 Part B).
 *
 * Everything the tree grants lands here and ONLY here: the tower pool filters to
 * unlocked towers, the class table to unlocked classes (locked classes then never tier,
 * however many spillover towers accumulate — R-08-19a), effect nodes ride
 * `runEffects` exactly like a daily mutator (R-08-22b), and start bonuses adjust the
 * opening numbers. Dailies never call this — they use DAILY_UNLOCKS (R-08-22).
 */
export function buildFreePlayConfig(mapId: string, unlocked: UnlockedContent): RunConfig {
  const classTable = buildClassTable();
  const allowedClasses = new Set<string>(unlocked.classes);
  const filteredTable: Record<string, ClassTable[string]> = {};
  for (const [id, spec] of Object.entries(classTable)) {
    if (allowedClasses.has(id)) filteredTable[id] = spec;
  }

  let gold = 0;
  let bench = 0;
  let coreHp = 0;
  let placements = 0;
  for (const bonus of unlocked.startBonuses) {
    if (bonus.stat === 'gold') gold += bonus.value;
    else if (bonus.stat === 'benchSlot') bench += bonus.value;
    else if (bonus.stat === 'coreHp') coreHp += bonus.value;
    else if (bonus.stat === 'placement') placements += bonus.value;
  }

  const base = buildRunConfig({ mapId, towerPool: unlocked.towers });
  return {
    ...base,
    startingGold: base.startingGold + gold,
    benchSlots: base.benchSlots + bench,
    coreMaxHp: base.coreMaxHp + coreHp,
    placementsPerRound: base.placementsPerRound + placements,
    classTable: filteredTable,
    runEffects: unlocked.runEffects,
  };
}

export function buildRunConfig(options: ScenarioOptions = {}): RunConfig {
  const supplied = options.map;
  const mapId = supplied?.id ?? options.mapId ?? 'the-furrow';
  const authored = supplied ? undefined : MAPS.find((m) => m.id === mapId);
  if (!supplied && !authored) throw new Error(`unknown map "${mapId}"`);
  const map = supplied ?? authored!;

  const waves = options.waves ?? WAVES;

  return {
    ...DEFAULT_RUN_CONFIG,
    mapId: map.id,
    isDaily: options.isDaily ?? false,
    coreMaxHp: BALANCE.CORE_MAX_HP,
    startingGold: BALANCE.STARTING_GOLD,
    benchSlots: BALANCE.BENCH_SLOTS,
    shopSlots: BALANCE.SHOP_SLOTS,
    placementsPerRound: BALANCE.PLACEMENTS_PER_ROUND,
    totalWaves: waves.length,
    paths: map.paths,
    core: map.core,
    waves,
    enemyTable: buildEnemyTable(),
    buildMask: map.buildMask,
    // Authored maps have none; a generated day supplies its own through `overrides`.
    buffTiles: [],
    towerPool: buildTowerPool(options.towerPool),
    classTable: buildClassTable(),
    balance: BALANCE,
    ...options.overrides,
  };
}
